import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ResidencyAddressForm } from '@app/features/residences/residency-address-form/residency-address-form';
import { Residency, ResidencyFormGroup } from '@app/features/residences/residency.model';
import { map, startWith } from 'rxjs';
import { residencyLabel } from '@app/features/residences/residency-label';
import { AssignmentType, assignmentTypeLabel } from './assignment-type.model';

export interface AssignmentFormValue {
  assignmentType?: AssignmentType | null;
  residencies?: Residency[] | null;
}
@Component({
  selector: 'app-assignment-form',
  imports: [
    MatRadioModule,
    ReactiveFormsModule,
    ResidencyAddressForm,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
  ],
  templateUrl: './assignment-form.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./assignment-form.scss'],
})
export class AssignmentForm implements OnInit {
  cdr = inject(ChangeDetectorRef);
  initWith = input.required<AssignmentFormValue>();
  validation = input<boolean>(false);
  readOnly = input<boolean>(false);
  /**
   * L'affectation d'avant un avenant (RG-avenant-consultation) : ce qui en diffère est surligné,
   * l'ancienne valeur en info-bulle.
   */
  previous = input<AssignmentFormValue | null>(null);
  form = new FormGroup({
    assignmentType: new FormControl<AssignmentType | null | undefined>('TOURNEE'),
    residencies: new FormArray<ResidencyFormGroup>([this.buildResidencyFormGroup()]),
  });

  readonly valueSig = toSignal(
    this.form.valueChanges.pipe(
      startWith(this.form.getRawValue()),
      map((v) => v as AssignmentFormValue),
    ),
    {
      initialValue: this.form.getRawValue() as AssignmentFormValue,
    },
  );

  constructor() {
    effect(() => this.valueSig());
    effect(() => {
      this.applyReadOnlyState(this.readOnly());
    });
    effect(() => {
      if (this.validation()) {
        this.form.markAllAsTouched();
      }
    });
  }

  ngOnInit(): void {
    this.form.controls.assignmentType.setValue(this.initWith()!.assignmentType);
    this.form.controls.residencies.clear();
    this.initWith()?.residencies?.forEach((residency) => {
      const residencyFormGroup = this.buildResidencyFormGroup();
      residencyFormGroup.setValue({
        name: residency.name,
        address: residency.address,
        postalCode: residency.postalCode,
        city: residency.city,
      });
      this.form.controls.residencies.push(residencyFormGroup);
    });
  }

  /** L'info-bulle du type d'affectation, s'il diffère d'avant l'avenant. */
  typeChange(): string | null {
    const previous = this.previous()?.assignmentType;
    const current = this.form.controls.assignmentType.value;
    return previous && current && previous !== current
      ? `Avant l'avenant : ${assignmentTypeLabel(previous)}`
      : null;
  }

  /** L'info-bulle d'une résidence que l'avenant ajoute. */
  residencyChange(index: number): string | null {
    const previous = this.previousResidencies();
    if (previous === null) {
      return null;
    }
    const residency = this.form.controls.residencies.at(index)?.getRawValue() as Residency;
    return residency && !previous.includes(residencyLabel(residency))
      ? "Ajoutée par l'avenant"
      : null;
  }

  /** Les résidences d'avant l'avenant qu'il retire. */
  removedResidencies(): string[] {
    const previous = this.previousResidencies();
    if (previous === null) {
      return [];
    }
    const current =
      this.form.controls.assignmentType.value === 'SPECIFIC'
        ? (this.form.controls.residencies.getRawValue() as Residency[]).map(residencyLabel)
        : [];
    return previous.filter((label) => !current.includes(label));
  }

  private previousResidencies(): string[] | null {
    const previous = this.previous();
    if (!previous) {
      return null;
    }
    return previous.assignmentType === 'SPECIFIC'
      ? (previous.residencies ?? []).map(residencyLabel)
      : [];
  }

  buildResidencyFormGroup(): ResidencyFormGroup {
    return new FormGroup({
      name: new FormControl<string | undefined>('', { nonNullable: true }),
      address: new FormControl('', { nonNullable: true }),
      postalCode: new FormControl('', {
        nonNullable: true,
        validators: [Validators.pattern(/^\d{5}$/)],
      }),
      city: new FormControl('', { nonNullable: true }),
    });
  }

  onAssignmentTypeChange(event: MatRadioChange<AssignmentType>) {
    if (this.readOnly()) {
      return;
    }

    const assignmentType = event.value;
    if (assignmentType === 'SPECIFIC') {
      this.form.setControl('residencies', new FormArray([this.buildResidencyFormGroup()]));
    } else {
      this.form.setControl('residencies', new FormArray([] as ResidencyFormGroup[]));
    }
    this.cdr.detectChanges();
  }

  addResidency() {
    if (this.readOnly()) {
      return;
    }

    this.form.controls.residencies.push(this.buildResidencyFormGroup());
  }

  removeResidency(index: number) {
    if (this.readOnly()) {
      return;
    }

    this.form.controls.residencies.removeAt(index);
  }

  private applyReadOnlyState(readOnly: boolean): void {
    if (readOnly) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });
  }

  onPreviousContractSelected(assignmentType: AssignmentType, residencies: Residency[]) {
    this.form.controls.assignmentType.setValue(assignmentType);
    const residenciesFormArray = new FormArray<ResidencyFormGroup>(
      residencies.map((residency) => {
        const residencyFormGroup = this.buildResidencyFormGroup();
        residencyFormGroup.setValue({
          name: residency.name,
          address: residency.address,
          postalCode: residency.postalCode,
          city: residency.city,
        });
        return residencyFormGroup;
      }),
    );
    this.form.setControl('residencies', residenciesFormArray);
  }
}
